<template>
  <div class="ai-quotas">
    <div class="ai-quota-stats">
      <article class="ai-quota-stat ai-quota-stat--primary">
        <span>Limite par défaut</span>
        <strong>{{ defaultLimit }}</strong>
        <small>requêtes / utilisateur</small>
      </article>
      <article class="ai-quota-stat">
        <span>Domaines</span>
        <strong>{{ scopedDomains.length }}</strong>
        <small>surveillés</small>
      </article>
      <article class="ai-quota-stat">
        <span>Personnalisés</span>
        <strong>{{ overrideCount }}</strong>
        <small>overrides actifs</small>
      </article>
      <article class="ai-quota-stat">
        <span>Utilisation mois</span>
        <strong>{{ totalUsageThisMonth }}</strong>
        <small>requêtes</small>
      </article>
    </div>

    <section class="ai-quota-table-card">
      <header class="ai-quota-table-header">
        <div>
          <h4>Pilotage des quotas</h4>
          <p>{{ filteredDomains.length }} domaine(s) affiché(s).</p>
        </div>
        <div class="ai-quota-table-actions">
          <p v-if="loading" class="ai-loading-copy">Chargement...</p>
          <label class="ai-search-field">
            <i class="fas fa-search" aria-hidden="true"></i>
            <t-input v-model="search" placeholder="Rechercher un domaine…"></t-input>
          </label>
        </div>
      </header>

      <div class="ai-quota-table-body">
        <b-table
          :data="filteredDomains"
          :loading="loading"
          hoverable
          paginated
          per-page="20"
          default-sort="label"
        >
          <b-table-column field="label" label="Domaine" sortable v-slot="props">
            <span class="ai-domain-name">{{ props.row.label }}</span>
          </b-table-column>

          <b-table-column field="domain_monthly_limit" label="Limite mensuelle du domaine" numeric sortable v-slot="props">
            <div class="ai-limit-cell">
              <t-input
                v-model.number="props.row.domain_monthly_limit"
                type="number"
                min="0"
                max="1000000"
                class="ai-limit-input"
                placeholder="Illimité"
              ></t-input>
            </div>
          </b-table-column>

          <b-table-column field="monthly_user_limit" label="Limite mensuelle par utilisateur" numeric sortable v-slot="props">
            <div class="ai-limit-cell">
              <t-input
                v-model.number="props.row.monthly_user_limit"
                type="number"
                min="0"
                max="1000000"
                class="ai-limit-input"
              ></t-input>
            </div>
          </b-table-column>

          <b-table-column field="questions_count_all" label="Questions posées all" numeric sortable v-slot="props">
            <span class="ai-usage-count">{{ props.row.questions_count_all }}</span>
          </b-table-column>

          <b-table-column field="conversations_count" label="Conversations lancées" numeric sortable v-slot="props">
            <span class="ai-usage-count">{{ props.row.conversations_count }}</span>
          </b-table-column>

          <b-table-column field="unique_users_count" label="Utilisateurs uniques" numeric sortable v-slot="props">
            <span class="ai-usage-count">{{ props.row.unique_users_count }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="210" v-slot="props">
            <div class="ai-quota-actions">
              <button
                type="button"
                class="ai-quota-button ai-quota-button--primary"
                :disabled="savingUuid === props.row.uuid"
                @click="save(props.row)"
              >
                Enregistrer
              </button>
              <button
                v-if="props.row.has_override"
                type="button"
                class="ai-quota-button"
                :disabled="savingUuid === props.row.uuid"
                @click="reset(props.row)"
              >
                Réinitialiser
              </button>
            </div>
          </b-table-column>

          <template #empty>
            <div class="ai-empty-table">Aucun domaine.</div>
          </template>
        </b-table>
      </div>
    </section>
  </div>
</template>

<script>
import aiService from '@/services/ai.service'

export default {
  name: 'app-ai-quotas',
  props: {
    aiDomain: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      loading: false,
      savingUuid: null,
      defaultLimit: 0,
      domains: [],
      search: ''
    }
  },
  computed: {
    domainUuid () {
      return this.aiDomain && this.aiDomain.uuid ? this.aiDomain.uuid : null
    },
    scopedDomains () {
      if (!this.domainUuid) {
        return this.domains
      }

      return this.domains.filter(domain => domain.uuid === this.domainUuid)
    },
    filteredDomains () {
      const term = this.search.trim().toLowerCase()
      if (!term) {
        return this.scopedDomains
      }

      return this.scopedDomains.filter(d => (d.label || '').toLowerCase().includes(term))
    },
    overrideCount () {
      return this.scopedDomains.filter(domain => domain.has_override).length
    },
    totalUsageThisMonth () {
      return this.scopedDomains.reduce((total, domain) => total + Number(domain.questions_count_all || domain.usage_this_month || 0), 0)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      aiService.listQuotas().then(data => {
        this.defaultLimit = data.default_limit
        this.domains = Array.isArray(data.domains) ? data.domains : []
      }).catch(err => {
        this.notifyError(err, 'Impossible de charger les quotas.')
      }).finally(() => {
        this.loading = false
      })
    },
    save (row) {
      const domainLimit = row.domain_monthly_limit === null || row.domain_monthly_limit === ''
        ? null
        : Number(row.domain_monthly_limit)
      const userLimit = Number(row.monthly_user_limit)
      if (domainLimit !== null && (!Number.isInteger(domainLimit) || domainLimit < 0)) {
        this.$buefy.toast.open({ message: 'La limite domaine doit être un entier positif ou vide.', type: 'is-warning' })
        return
      }
      if (!Number.isInteger(userLimit) || userLimit < 0) {
        this.$buefy.toast.open({ message: 'La limite utilisateur doit être un entier positif.', type: 'is-warning' })
        return
      }
      this.savingUuid = row.uuid
      aiService.setDomainQuota(row.uuid, {
        domainMonthlyLimit: domainLimit,
        monthlyUserLimit: userLimit
      }).then(result => {
        row.monthly_limit = result.monthly_limit
        row.domain_monthly_limit = result.domain_monthly_limit
        row.monthly_user_limit = result.monthly_user_limit
        row.has_override = result.has_override
        this.$buefy.notification.open({
          duration: 3000,
          message: `Quotas de <strong>${row.label}</strong> mis à jour.`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
      }).catch(err => {
        this.notifyError(err, `Impossible d'enregistrer le quota de ${row.label}.`)
      }).finally(() => {
        this.savingUuid = null
      })
    },
    reset (row) {
      this.savingUuid = row.uuid
      aiService.clearDomainQuota(row.uuid).then(result => {
        row.monthly_limit = result.monthly_limit
        row.domain_monthly_limit = result.domain_monthly_limit
        row.monthly_user_limit = result.monthly_user_limit
        row.has_override = result.has_override
        this.$buefy.notification.open({
          duration: 3000,
          message: `<strong>${row.label}</strong> est revenu à la limite par défaut (${result.monthly_limit}).`,
          position: 'is-bottom-right',
          type: 'is-info',
          hasIcon: false
        })
      }).catch(err => {
        this.notifyError(err, `Impossible de réinitialiser le quota de ${row.label}.`)
      }).finally(() => {
        this.savingUuid = null
      })
    },
    notifyError (err, fallback) {
      const status = err && err.response ? err.response.status : null
      this.$buefy.notification.open({
        duration: 4500,
        message: status === 403
          ? 'Vous n\'avez pas le rôle « ai-admin » requis pour gérer les quotas.'
          : fallback,
        position: 'is-bottom-left',
        type: 'is-danger',
        hasIcon: false
      })
    }
  }
}
</script>

<style scoped>
.ai-quotas {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ai-quota-table-header h4 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
}

.ai-quota-table-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.875rem;
}

.ai-search-field {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 18rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #f8fafc;
  color: #64748b;
  padding: 0.45rem 0.55rem;
}

.ai-search-field i {
  flex: 0 0 auto;
}

.ai-search-field > *:last-child {
  flex: 1 1 auto;
}

.ai-quota-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.ai-quota-stat {
  min-height: 5.3rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.85rem;
}

.ai-quota-stat--primary {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.ai-quota-stat span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.1;
}

.ai-quota-stat strong {
  display: block;
  margin-top: 0.4rem;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

.ai-quota-stat small {
  display: block;
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 700;
}

.ai-quota-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #ffffff;
}

.ai-quota-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem;
}

.ai-quota-table-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.ai-loading-copy {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 0.875rem;
}

.ai-quota-table-body {
  overflow-x: auto;
  padding: 0.75rem;
}

.ai-domain-name,
.ai-usage-count {
  color: #111827;
  font-weight: 500;
}

.ai-usage-count {
  font-weight: 700;
}

.ai-limit-input {
  width: 7.5rem;
}

.ai-limit-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 12.5rem;
}

.ai-quota-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.ai-quota-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.2rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #334155;
  font-size: 0.8125rem;
  font-weight: 800;
  padding: 0.45rem 0.65rem;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ai-quota-button:hover:not(:disabled) {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.ai-quota-button--primary {
  border-color: #1d4ed8;
  background: #2563eb;
  color: #ffffff;
}

.ai-quota-button--primary:hover:not(:disabled) {
  border-color: #1e40af;
  background: #1d4ed8;
  color: #ffffff;
}

.ai-quota-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ai-empty-table {
  padding: 2rem 1rem;
  color: #94a3b8;
  text-align: center;
}

@media (max-width: 1024px) {
  .ai-quota-table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .ai-quota-table-actions,
  .ai-search-field {
    width: 100%;
    min-width: 0;
  }

  .ai-quota-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .ai-quota-stats {
    grid-template-columns: 1fr;
  }
}
</style>
